"use client"

import { useLocation } from "@tanstack/react-router"
import { ChevronRight, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"

export function NavMain({
  items,
  prependItem,
}: {
  items: {
    title: string
    url: string
    icon?: LucideIcon
    isActive?: boolean
    items?: {
      title: string
      url: string
    }[]
  }[]
  prependItem?: ReactNode
}) {
  const { toggleSidebar, open } = useSidebar()
  const location = useLocation()

  const isItemActive = (itemUrl: string) => {
    if (itemUrl === "#") return false
    return location.pathname === itemUrl
  }

  const hasActiveSubItem = (item: (typeof items)[0]) => {
    if (!item.items) return false
    return item.items.some((subItem) => isItemActive(subItem.url))
  }
  return (
    <SidebarGroup>
      {/* <SidebarGroupLabel>General</SidebarGroupLabel> */}
      <SidebarMenu>
        {prependItem}
        {items.map((item) =>
          item.items ? (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={hasActiveSubItem(item)}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger
                  asChild
                  onClick={(event) => {
                    if (!open) {
                      event.preventDefault()
                      toggleSidebar()
                      // TODO: also trigger the collapsible open?
                    }
                  }}
                >
                  <SidebarMenuButton
                    tooltip={item.title}
                    isActive={isItemActive(item.url) || hasActiveSubItem(item)}
                  >
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild isActive={isItemActive(subItem.url)}>
                          <a href={subItem.url}>
                            <span>{subItem.title}</span>
                          </a>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild tooltip={item.title} isActive={isItemActive(item.url)}>
                <a href={item.url}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ),
        )}
      </SidebarMenu>
    </SidebarGroup>
  )
}
